/**
 * Question Mapping Registry
 *
 * Single source of truth for how every UI question maps to the engine model.
 * This registry is used for:
 * - Runtime validation (catching enum mismatches)
 * - CI audit (ensuring all questions are mapped)
 * - DevTools sidebar (showing live engine model)
 */

import type { QuestionMapping, QuestionSection } from './types';
import { UI_MAPPING_VERSION } from '../contracts/dataContract';

/**
 * Contract version for the UI→engine mapping registry.
 *
 * Bump rules (mirrors engine RULESET_VERSION convention):
 * - PATCH: label/notes tweaks, no structural changes
 * - MINOR: new fields, enum additions (backwards-compatible)
 * - MAJOR: fields removed or renamed, breaking changes
 *
 * Keep in sync with RULESET_VERSION in src/core/rulesetVersion.ts.
 * A mismatch between MAPPING_CONTRACT_VERSION and the engine's
 * rulesetVersion shown in DevOverlay indicates the UI normaliser
 * and engine are operating on different contracts.
 */
export const MAPPING_CONTRACT_VERSION = UI_MAPPING_VERSION;

/**
 * All question mappings in the application.
 * Every question that collects data must be registered here.
 */
export const questionMappings: QuestionMapping[] = [
  // ============================================================================
  // HOME SECTION
  // ============================================================================
  {
    questionId: 'propertyType',
    label: 'Property type',
    section: 'home',
    rawPaths: ['propertyType'],
    normalizedPaths: ['propertyType'],
    engineImpact: ['heat loss calculation', 'space availability assessment'],
    enums: {
      'propertyType': [
        'flat', 'maisonette', 'terraced', 'end_terrace', 'semi',
        'detached', 'bungalow', 'unknown'
      ]
    },
    required: true,
  },
  {
    questionId: 'propertyAgeBand',
    label: 'Property age',
    section: 'home',
    rawPaths: ['propertyAgeBand'],
    normalizedPaths: ['propertyAgeBand'],
    engineImpact: ['heat loss calculation', 'insulation assumptions'],
    enums: {
      'propertyAgeBand': [
        'pre_1919', '1919_1944', '1945_1964', '1965_1980',
        '1981_2000', '2001_2010', '2011_plus', 'unknown'
      ]
    },
    required: true,
  },
  {
    questionId: 'bedrooms',
    label: 'Number of bedrooms',
    section: 'home',
    rawPaths: ['bedrooms'],
    normalizedPaths: ['bedrooms'],
    engineImpact: ['heat demand calculation', 'hot water sizing'],
    enums: {
      'bedrooms': ['1', '2', '3', '4', '5']
    },
    required: true,
  },
  {
    questionId: 'bathrooms',
    label: 'Number of bathrooms',
    section: 'home',
    rawPaths: ['bathrooms'],
    normalizedPaths: ['bathrooms'],
    engineImpact: ['hot water demand calculation', 'cylinder sizing'],
    enums: {
      'bathrooms': ['1', '2', '3', '4']
    },
    required: true,
  },
  {
    questionId: 'availableSpace',
    label: 'Available space for equipment',
    section: 'home',
    rawPaths: ['availableSpace'],
    normalizedPaths: ['spaceAvailability'],
    engineImpact: ['cylinder placement', 'system type eligibility'],
    enums: {
      'availableSpace': [
        'airing_cupboard_loft', 'airing_cupboard', 'loft',
        'garage', 'limited', 'none', 'unknown'
      ]
    },
  },
  {
    questionId: 'outdoorSpace',
    label: 'Outdoor space',
    section: 'home',
    rawPaths: ['outdoorSpace'],
    normalizedPaths: [], // Not directly mapped - used for heat pump suitability
    engineImpact: ['heat pump placement feasibility'],
    enums: {
      'outdoorSpace': [
        'rear_limited', 'rear_good', 'front_rear',
        'none', 'shared', 'unknown'
      ]
    },
    notes: 'Affects heat pump eligibility but not directly mapped to SystemRecInput',
  },

  // ============================================================================
  // OCCUPANCY SECTION
  // ============================================================================
  {
    questionId: 'occupancyCount',
    label: 'Number of occupants',
    section: 'occupancy',
    rawPaths: ['occupancyCount'],
    normalizedPaths: ['occupancy'],
    engineImpact: ['hot water demand', 'heating pattern assumptions'],
    enums: {
      'occupancyCount': ['1', '2', '3', '4', '5', '6']
    },
    required: true,
  },
  {
    questionId: 'occupancyTiming',
    label: 'When home is occupied',
    section: 'occupancy',
    rawPaths: ['occupancyTiming'],
    normalizedPaths: ['occupancyTiming'],
    engineImpact: ['heating schedule optimization', 'setback tolerance'],
    enums: {
      'occupancyTiming': [
        'mornings_evenings', 'evenings_weekends',
        'most_of_day', 'variable', 'unknown'
      ]
    },
  },
  {
    questionId: 'temporaryGuests',
    label: 'Temporary guests',
    section: 'occupancy',
    rawPaths: ['temporaryGuests'],
    normalizedPaths: ['temporaryGuests'],
    engineImpact: ['peak hot water demand'],
    enums: {
      'temporaryGuests': ['none', 'occasional', 'frequent', 'permanent_addition']
    },
  },
  {
    questionId: 'futureDemand',
    label: 'Future demand changes',
    section: 'occupancy',
    rawPaths: ['futureDemand'],
    normalizedPaths: ['futureDemand'],
    engineImpact: ['system sizing headroom'],
    enums: {
      'futureDemand': ['decreasing', 'stable', 'increasing', 'temporary_increase']
    },
  },

  // ============================================================================
  // WATER SECTION
  // ============================================================================
  {
    questionId: 'hotWaterDemand',
    label: 'Hot water demand level',
    section: 'water',
    rawPaths: ['hotWaterDemand'],
    normalizedPaths: ['hotWaterDemand'],
    engineImpact: ['cylinder sizing', 'combi eligibility', 'recovery time'],
    enums: {
      'hotWaterDemand': ['low', 'medium', 'high']
    },
    required: true,
  },
  {
    questionId: 'usagePattern',
    label: 'Hot water usage pattern',
    section: 'water',
    rawPaths: ['usagePattern'],
    normalizedPaths: [], // Presentation only - affects UX not engine
    enums: {
      'usagePattern': ['mostly_showers', 'mostly_baths', 'mixed']
    },
    uiOnly: true,
    notes: 'Used for UI guidance, not directly in engine calculations',
  },
  {
    questionId: 'usageFrequency',
    label: 'Hot water usage frequency',
    section: 'water',
    rawPaths: ['usageFrequency'],
    normalizedPaths: [], // Presentation only
    enums: {
      'usageFrequency': ['minimal', 'once_daily', 'multiple_daily', 'high_demand']
    },
    uiOnly: true,
  },

  // ============================================================================
  // ENERGY/SYSTEM SECTION
  // ============================================================================
  {
    questionId: 'boilerType',
    label: 'Current boiler type',
    section: 'energy',
    rawPaths: ['boilerType'],
    normalizedPaths: ['currentBoiler'],
    engineImpact: ['baseline calculation', 'upgrade path analysis'],
    enums: {
      'boilerType': [
        'combi', 'storage_combi', 'system', 'regular',
        'air_source_hp', 'ground_source_hp', 'electric',
        'storage_heaters', 'unknown'
      ]
    },
    required: true,
  },
  {
    questionId: 'waterSystem',
    label: 'Current hot water system',
    section: 'energy',
    rawPaths: ['waterSystem'],
    normalizedPaths: ['currentWater'],
    engineImpact: ['baseline calculation', 'upgrade path analysis'],
    enums: {
      'waterSystem': [
        'on_demand', 'unvented', 'open_vented', 'mixergy_unvented',
        'mixergy_open', 'heat_battery', 'thermal_store', 'unknown'
      ]
    },
    required: true,
  },
  {
    questionId: 'electricalCapacity',
    label: 'Electrical capacity',
    section: 'energy',
    rawPaths: ['electricalCapacity'],
    normalizedPaths: ['electricalCapacityConfidence'],
    engineImpact: ['heat pump eligibility', 'unvented cylinder eligibility'],
    enums: {
      'electricalCapacity': ['unknown', 'standard', 'upgraded', 'three_phase', 'limited']
    },
  },
  {
    questionId: 'mainsPressure',
    label: 'Mains water pressure',
    section: 'energy',
    rawPaths: ['mainsPressure'],
    normalizedPaths: ['mainsPressure'],
    engineImpact: ['unvented eligibility', 'combi flow rate'],
    enums: {
      'mainsPressure': ['not_tested', 'adequate', 'low', 'high']
    },
  },
  {
    questionId: 'flowRate',
    label: 'Flow rate',
    section: 'energy',
    rawPaths: ['flowRate'],
    normalizedPaths: ['flowRate'],
    engineImpact: ['combi sizing', 'shower performance'],
    enums: {
      'flowRate': ['8', '12', '18', '24', '30', 'unknown']
    },
  },
  {
    questionId: 'drawsPerDay',
    label: 'Hot water draws per day',
    section: 'energy',
    rawPaths: ['drawsPerDay'],
    normalizedPaths: ['drawsPerDay'],
    engineImpact: ['hot water demand calculation', 'cylinder recovery'],
    enums: {
      'drawsPerDay': ['8', '15', '22', '30', '40']
    },
  },
  {
    questionId: 'mainsFlowLpm',
    label: 'Mains flow rate (L/min)',
    section: 'energy',
    rawPaths: ['mainsFlowLpm'],
    normalizedPaths: ['mainsFlowLpm'],
    engineImpact: ['combi suitability', 'unvented cylinder performance'],
  },
  {
    questionId: 'mainsStaticPressureBar',
    label: 'Mains static pressure (bar)',
    section: 'energy',
    rawPaths: ['mainsStaticPressureBar'],
    normalizedPaths: ['mainsStaticPressureBar'],
    engineImpact: ['unvented eligibility', 'combi performance'],
  },
  {
    questionId: 'mainsDynamicFlowLpm',
    label: 'Mains dynamic flow (L/min)',
    section: 'energy',
    rawPaths: ['mainsDynamicFlowLpm'],
    normalizedPaths: ['mainsDynamicFlowLpm'],
    engineImpact: ['hot water performance assessment'],
  },
  {
    questionId: 'mainsDynamicPressureBar',
    label: 'Mains dynamic pressure (bar)',
    section: 'energy',
    rawPaths: ['mainsDynamicPressureBar'],
    normalizedPaths: ['mainsDynamicPressureBar'],
    engineImpact: ['hot water performance assessment'],
  },
  {
    questionId: 'coldMainType',
    label: 'Cold main type',
    section: 'energy',
    rawPaths: ['coldMainType'],
    normalizedPaths: ['coldMainType'],
    engineImpact: ['water supply characteristics'],
    enums: {
      'coldMainType': ['mdpe', 'copper', 'lead', 'unknown']
    },
  },

  // ============================================================================
  // BASELINE SECTION
  // ============================================================================
  {
    questionId: 'systemAge',
    label: 'System age',
    section: 'baseline',
    rawPaths: ['systemAge'],
    normalizedPaths: ['existingSystemAge'],
    engineImpact: ['baseline efficiency', 'replacement urgency'],
    enums: {
      'systemAge': ['0-5', '5-10', '10-15', '15-20', '20+', 'unknown']
    },
  },
  {
    questionId: 'efficiencySource',
    label: 'Efficiency data source',
    section: 'baseline',
    rawPaths: ['efficiencySource'],
    normalizedPaths: ['currentSystemEfficiency.source'],
    engineImpact: ['baseline confidence level'],
    enums: {
      'efficiencySource': ['sedbuk', 'epc', 'erp', 'unknown']
    },
  },
  {
    questionId: 'sedbukBand',
    label: 'SEDBUK efficiency band',
    section: 'baseline',
    rawPaths: ['sedbukBand'],
    normalizedPaths: ['currentSystemEfficiency.sedbukBand', 'currentSystemEfficiency.sedbukSeasonal'],
    engineImpact: ['baseline annual cost', 'savings calculation'],
    enums: {
      'sedbukBand': ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'unknown']
    },
  },
  {
    questionId: 'gcNumber',
    label: 'GC number',
    section: 'baseline',
    rawPaths: ['gcNumber'],
    normalizedPaths: ['currentSystemEfficiency.gcNumber'],
    engineImpact: ['SEDBUK lookup', 'exact efficiency determination'],
    notes: 'Free text field, no enum validation',
  },
  {
    questionId: 'boilerMake',
    label: 'Boiler manufacturer',
    section: 'baseline',
    rawPaths: ['boilerMake'],
    normalizedPaths: ['currentSystemEfficiency.boilerMake'],
    engineImpact: ['SEDBUK lookup'],
    notes: 'Free text field',
  },
  {
    questionId: 'boilerModel',
    label: 'Boiler model',
    section: 'baseline',
    rawPaths: ['boilerModel'],
    normalizedPaths: ['currentSystemEfficiency.boilerModel'],
    engineImpact: ['SEDBUK lookup'],
    notes: 'Free text field',
  },
  {
    questionId: 'epcBand',
    label: 'EPC band',
    section: 'baseline',
    rawPaths: ['epcBand'],
    normalizedPaths: ['currentSystemEfficiency.epcBand'],
    engineImpact: ['efficiency fallback'],
    enums: {
      'epcBand': ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'unknown']
    },
  },
  {
    questionId: 'currentFuelType',
    label: 'Current fuel type',
    section: 'baseline',
    rawPaths: ['currentFuelType'],
    normalizedPaths: ['currentFuelType'],
    engineImpact: ['baseline cost calculation', 'carbon calculation'],
    enums: {
      'currentFuelType': ['gas', 'lpg', 'oil', 'electric']
    },
    required: true,
  },
  {
    questionId: 'sludgeRisk',
    label: 'Sludge contamination risk',
    section: 'baseline',
    rawPaths: ['sludgeRisk'],
    normalizedPaths: ['sludgeRisk'],
    engineImpact: ['clean & flush recommendation', 'red flags'],
    enums: {
      'sludgeRisk': ['none', 'possible', 'likely', 'confirmed']
    },
  },

  // ============================================================================
  // PLANS SECTION
  // ============================================================================
  {
    questionId: 'lengthOfStay',
    label: 'Expected length of stay',
    section: 'plans',
    rawPaths: ['lengthOfStay'],
    normalizedPaths: ['expectedLengthOfStay'],
    engineImpact: ['payback period relevance', 'longevity weighting'],
    enums: {
      'lengthOfStay': ['short', 'medium', 'long', 'permanent']
    },
    required: true,
  },
  {
    questionId: 'lifeStage',
    label: 'Life stage',
    section: 'plans',
    rawPaths: ['lifeStage'],
    normalizedPaths: [], // UI presentation only
    enums: {
      'lifeStage': ['young_family', 'established', 'empty_nesters', 'retired', 'other']
    },
    uiOnly: true,
    notes: 'Used for narrative framing, not engine calculation',
  },
  {
    questionId: 'budgetPriority',
    label: 'Budget priority',
    section: 'plans',
    rawPaths: ['budgetPriority'],
    normalizedPaths: ['costPriority'],
    engineImpact: ['option scoring weights'],
    enums: {
      'budgetPriority': ['upfront_cost', 'balanced', 'ongoing_cost']
    },
    required: true,
  },
  {
    questionId: 'disruptionTolerance',
    label: 'Disruption tolerance',
    section: 'plans',
    rawPaths: ['disruptionTolerance'],
    normalizedPaths: ['disruptionTolerance'],
    engineImpact: ['system change feasibility', 'option filtering'],
    enums: {
      'disruptionTolerance': ['one_room_only', 'two_three_rooms', 'major_whole_house']
    },
    required: true,
  },
  {
    questionId: 'urgency',
    label: 'Replacement urgency',
    section: 'plans',
    rawPaths: ['urgency'],
    normalizedPaths: ['urgency'],
    engineImpact: ['recommendation framing', 'option prioritization'],
    enums: {
      'urgency': ['emergency', 'soon', 'planning', 'future']
    },
    required: true,
  },
  {
    questionId: 'upgradePriorities',
    label: 'Upgrade priorities',
    section: 'plans',
    rawPaths: ['upgradePriorities'],
    normalizedPaths: ['upgradePriorities'],
    engineImpact: ['option scoring weights', 'fit assessment'],
    notes: 'Multi-dimensional priority ranking (ordered priority stack)',
  },

  // ============================================================================
  // EMITTERS SECTION
  // ============================================================================
  {
    questionId: 'emitterType',
    label: 'Emitter type',
    section: 'emitters',
    rawPaths: ['emitterType'],
    normalizedPaths: ['emitters.type', 'heatEmitterType'],
    engineImpact: ['heat pump suitability', 'low-temp readiness'],
    enums: {
      'emitterType': ['radiators', 'underfloor', 'mixed', 'unknown']
    },
  },
  {
    questionId: 'radiatorSize',
    label: 'Radiator size',
    section: 'emitters',
    rawPaths: ['radiatorSize'],
    normalizedPaths: ['heatEmitterType'],
    engineImpact: ['low-temp suitability', 'heat pump sizing'],
    enums: {
      'radiatorSize': ['small', 'typical', 'oversized', 'unknown']
    },
    normalize: (raw) => {
      switch (raw.radiatorSize) {
        case 'oversized':
          return { heatEmitterType: 'oversized_rads' };
        case 'typical':
        case 'small':
          return { heatEmitterType: 'standard_rads' };
        case 'unknown':
          return { heatEmitterType: 'standard_rads' };
        default:
          // Unexpected value - fallback to standard
          return { heatEmitterType: 'standard_rads' };
      }
    },
  },
  {
    questionId: 'ufhExtent',
    label: 'Underfloor heating extent',
    section: 'emitters',
    rawPaths: ['ufhExtent'],
    normalizedPaths: ['heatEmitterType'],
    engineImpact: ['low-temp readiness'],
    enums: {
      'ufhExtent': ['none', 'partial', 'full']
    },
  },
  {
    questionId: 'emitterLowTempSuitability',
    label: 'Low temperature suitability',
    section: 'emitters',
    rawPaths: ['emitterLowTempSuitability'],
    normalizedPaths: ['emitters.lowTempSuitability'],
    engineImpact: ['heat pump SCOP assumptions', 'upgrade cost'],
    enums: {
      // Matches engine lowTempSuitability values (see performanceClaims.ts isLowTempConfirmed)
      'emitterLowTempSuitability': [
        'likely_ok', 'likely_needs_upgrade',
        'definitely_needs_upgrade', 'unknown'
      ]
    },
    notes: 'UI maps lowTempCapability=yes → likely_ok, no → likely_needs_upgrade',
  },
  {
    questionId: 'emitterRoomsStruggle',
    label: 'Rooms struggle to heat',
    section: 'emitters',
    rawPaths: ['emitterRoomsStruggle'],
    normalizedPaths: ['emitters.roomsStruggleToHeat'],
    engineImpact: ['red flag generation', 'heat loss indication'],
    enums: {
      'emitterRoomsStruggle': ['yes', 'no', 'unknown']
    },
  },
  {
    questionId: 'pipeBore',
    label: 'Pipe bore size',
    section: 'emitters',
    rawPaths: ['pipeBore'],
    normalizedPaths: ['emitters.pipeBore'],
    engineImpact: ['flow rate constraints', 'heat pump compatibility'],
    enums: {
      'pipeBore': ['8mm', '10mm', '15mm', '22mm', 'unknown']
    },
  },

  // ============================================================================
  // RETROFIT SECTION
  // ============================================================================
  {
    questionId: 'insulationLevel',
    label: 'Overall insulation level',
    section: 'retrofit',
    rawPaths: ['insulationLevel'],
    normalizedPaths: ['insulationLevel', 'retrofit.loftInsulation', 'retrofit.wallInsulation', 'retrofit.windows', 'retrofit.draughtProofing'],
    engineImpact: ['heat loss calculation', 'heat pump sizing'],
    enums: {
      'insulationLevel': ['good', 'average', 'poor', 'unknown']
    },
    notes: 'Simplified field that maps to all retrofit sub-fields when specific data not available',
  },
  {
    questionId: 'retrofitLoftInsulation',
    label: 'Loft insulation',
    section: 'retrofit',
    rawPaths: ['retrofitLoftInsulation'],
    normalizedPaths: ['retrofit.loftInsulation'],
    engineImpact: ['heat loss calculation'],
    enums: {
      'retrofitLoftInsulation': ['none', 'some', 'good', 'unknown']
    },
  },
  {
    questionId: 'retrofitWallInsulation',
    label: 'Wall insulation',
    section: 'retrofit',
    rawPaths: ['retrofitWallInsulation'],
    normalizedPaths: ['retrofit.wallInsulation'],
    engineImpact: ['heat loss calculation'],
    enums: {
      'retrofitWallInsulation': ['none', 'cavity', 'solid_or_internal_or_external', 'unknown']
    },
  },
  {
    questionId: 'retrofitWindows',
    label: 'Windows',
    section: 'retrofit',
    rawPaths: ['retrofitWindows'],
    normalizedPaths: ['retrofit.windows'],
    engineImpact: ['heat loss calculation'],
    enums: {
      'retrofitWindows': ['single', 'older_double', 'modern_double_or_triple', 'unknown']
    },
  },
  {
    questionId: 'retrofitDraughtProofing',
    label: 'Draught proofing',
    section: 'retrofit',
    rawPaths: ['retrofitDraughtProofing'],
    normalizedPaths: ['retrofit.draughtProofing'],
    engineImpact: ['heat loss calculation'],
    enums: {
      'retrofitDraughtProofing': ['none', 'partial', 'modern', 'unknown']
    },
  },

  // ============================================================================
  // EXPERIENCE SECTION
  // ============================================================================
  {
    questionId: 'simultaneousHotWaterUse',
    label: 'Simultaneous hot water usage',
    section: 'experience',
    rawPaths: ['simultaneousHotWaterUse'],
    normalizedPaths: ['experienceQuestions.simultaneousHotWater'],
    engineImpact: ['experience fit scoring', 'combi suitability'],
    enums: {
      'simultaneousHotWaterUse': ['never', 'occasionally', 'most_days']
    },
  },
  {
    questionId: 'heatingContinuityImportance',
    label: 'Heating continuity importance',
    section: 'experience',
    rawPaths: ['heatingContinuityImportance'],
    normalizedPaths: ['experienceQuestions.heatingDuringHotWater'],
    engineImpact: ['experience fit scoring'],
    enums: {
      'heatingContinuityImportance': ['not_important', 'important', 'very_important']
    },
  },
  {
    questionId: 'airingCupboardUse',
    label: 'Airing cupboard use',
    section: 'experience',
    rawPaths: ['airingCupboardUse'],
    normalizedPaths: ['experienceQuestions.airingCupboard'],
    engineImpact: ['experience fit scoring', 'cylinder recommendation'],
    enums: {
      'airingCupboardUse': ['no', 'occasionally', 'yes_regularly']
    },
  },
  {
    questionId: 'spacePressure',
    label: 'Space pressure',
    section: 'experience',
    rawPaths: ['spacePressure'],
    normalizedPaths: ['experienceQuestions.spacePressure'],
    engineImpact: ['experience fit scoring', 'system type filtering'],
    enums: {
      'spacePressure': ['very_tight', 'some_flexibility', 'plenty']
    },
  },
  {
    questionId: 'operatingStylePreference',
    label: 'Operating style preference',
    section: 'experience',
    rawPaths: ['operatingStylePreference'],
    normalizedPaths: ['experienceQuestions.operatingStyle'],
    engineImpact: ['experience fit scoring', 'heat pump suitability'],
    enums: {
      'operatingStylePreference': ['quick_response', 'balanced', 'steady_consistent']
    },
  },
  {
    questionId: 'noiseSensitivity',
    label: 'Noise sensitivity',
    section: 'experience',
    rawPaths: ['noiseSensitivity'],
    normalizedPaths: ['experienceQuestions.noiseSensitivity'],
    engineImpact: ['experience fit scoring', 'heat pump suitability'],
    enums: {
      'noiseSensitivity': ['not_bothered', 'somewhat', 'very_sensitive']
    },
  },
  {
    questionId: 'environmentalIntent',
    label: 'Environmental priority',
    section: 'experience',
    rawPaths: ['environmentalIntent'],
    normalizedPaths: ['experienceQuestions.environmentalIntent'],
    engineImpact: ['experience fit scoring', 'heat pump weighting'],
    enums: {
      'environmentalIntent': ['nice_bonus', 'ready_for_later', 'greenest_now']
    },
  },
  {
    questionId: 'solarIntent',
    label: 'Solar PV plans',
    section: 'experience',
    rawPaths: ['solarIntent'],
    normalizedPaths: ['solarIntent'],
    engineImpact: ['heat pump economics', 'future-proofing score'],
    enums: {
      'solarIntent': ['none', 'planned', 'installed', 'unknown']
    },
  },

  // ============================================================================
  // SYSTEM PREFERENCES SECTION
  // ============================================================================
  {
    questionId: 'considerCombi',
    label: 'Consider combi boiler',
    section: 'preferences',
    rawPaths: ['considerCombi'],
    normalizedPaths: ['compareOptions'],
    enums: {
      'considerCombi': ['yes', 'no']
    },
    uiOnly: true,
    notes: 'Affects which options are generated, not a direct engine input',
  },
  {
    questionId: 'considerSystem',
    label: 'Consider system boiler',
    section: 'preferences',
    rawPaths: ['considerSystem'],
    normalizedPaths: ['compareOptions'],
    enums: {
      'considerSystem': ['yes', 'no']
    },
    uiOnly: true,
  },
  {
    questionId: 'considerHeatPump',
    label: 'Consider heat pump',
    section: 'preferences',
    rawPaths: ['considerHeatPump'],
    normalizedPaths: ['compareOptions'],
    enums: {
      'considerHeatPump': ['yes', 'no']
    },
    uiOnly: true,
  },
  {
    questionId: 'selectedCompareOptions',
    label: 'Selected comparison options',
    section: 'preferences',
    rawPaths: ['selectedCompareOptions'],
    normalizedPaths: ['compareOptions'],
    notes: 'Array of {boiler, water} objects',
  },

  // ============================================================================
  // THERMOSTAT PREFERENCES SECTION
  // ============================================================================
  {
    questionId: 'includeThermostat',
    label: 'Include thermostat recommendation',
    section: 'preferences',
    rawPaths: ['includeThermostat'],
    normalizedPaths: ['thermostatPreference.includeThermostat'],
    enums: {
      'includeThermostat': ['yes', 'no']
    },
  },
  {
    questionId: 'currentThermostatType',
    label: 'Current thermostat type',
    section: 'preferences',
    rawPaths: ['currentThermostatType'],
    normalizedPaths: ['currentThermostatType', 'thermostatPreference.existingThermostatType'],
    enums: {
      'currentThermostatType': ['mechanical', 'basic_digital', 'smart_on_off', 'smart_load_comp']
    },
  },
  {
    questionId: 'thermostatBudget',
    label: 'Thermostat budget',
    section: 'preferences',
    rawPaths: ['thermostatBudget'],
    normalizedPaths: ['thermostatPreference.budgetMax'],
    enums: {
      'thermostatBudget': ['basic', 'mid', 'premium']
    },
  },
  {
    questionId: 'wantLoadCompensation',
    label: 'Want load compensation',
    section: 'preferences',
    rawPaths: ['wantLoadCompensation'],
    normalizedPaths: ['thermostatPreference.requiredFeatures.loadCompensation', 'thermostatPreference.preferredControlType'],
    enums: {
      'wantLoadCompensation': ['yes', 'no', 'unsure']
    },
  },
  {
    questionId: 'wantSmartStart',
    label: 'Want smart start',
    section: 'preferences',
    rawPaths: ['wantSmartStart'],
    normalizedPaths: ['thermostatPreference.requiredFeatures.smartStart'],
    enums: {
      'wantSmartStart': ['yes', 'no', 'unsure']
    },
  },
  {
    questionId: 'wantMultiZone',
    label: 'Want multi-zone control',
    section: 'preferences',
    rawPaths: ['wantMultiZone'],
    normalizedPaths: ['thermostatPreference.requiredFeatures.multiZone'],
    enums: {
      'wantMultiZone': ['yes', 'no']
    },
  },

  // ============================================================================
  // LCOO (LIFECYCLE COST) SECTION
  // Note: LCOO data is passed through via SystemRecInput's index signature [key: string]: unknown
  // These fields are dynamically constructed in convertToSystemRecInput
  // ============================================================================
  {
    questionId: 'enableLcoo',
    label: 'Enable lifecycle cost analysis',
    section: 'lcoo',
    rawPaths: ['enableLcoo'],
    normalizedPaths: [], // Passed through via dynamic lcoo object
    enums: {
      'enableLcoo': ['yes', 'no']
    },
    notes: 'When enabled, constructs lcoo object passed to engine via index signature',
  },
  {
    questionId: 'lcooPeriodYears',
    label: 'LCOO analysis period',
    section: 'lcoo',
    rawPaths: ['lcooPeriodYears'],
    normalizedPaths: [], // Passed through via dynamic lcoo object
    enums: {
      'lcooPeriodYears': ['20', '25', '30']
    },
  },
  {
    questionId: 'lcooGasTariff',
    label: 'Gas tariff (p/kWh)',
    section: 'lcoo',
    rawPaths: ['lcooGasTariff'],
    normalizedPaths: [], // Passed through via dynamic lcoo object
    notes: 'Numeric field - passed via lcoo.tariffs.gasPPerKwh',
  },
  {
    questionId: 'lcooElecTariff',
    label: 'Electricity tariff (p/kWh)',
    section: 'lcoo',
    rawPaths: ['lcooElecTariff'],
    normalizedPaths: [], // Passed through via dynamic lcoo object
    notes: 'Numeric field - passed via lcoo.tariffs.elecPPerKwh',
  },

  // ============================================================================
  // SECTION STATUS (UI-only)
  // ============================================================================
  {
    questionId: 'sectionStatuses',
    label: 'Section completion statuses',
    section: 'home',
    rawPaths: ['sectionStatuses'],
    normalizedPaths: [],
    uiOnly: true,
    notes: 'Tracks which sections user has confirmed - UI state only',
  },

  // ============================================================================
  // ENERGY TARIFF SECTION
  // Required by engine for accurate heat pump economics (tariffProfile enum)
  // ============================================================================
  {
    questionId: 'tariffProfile',
    label: 'Electricity tariff profile',
    section: 'energy',
    rawPaths: ['tariffProfile'],
    normalizedPaths: ['tariffProfile'],
    engineImpact: ['heat pump running cost', 'annualised economics'],
    enums: {
      // Matches engine tariffProfile enum exactly
      'tariffProfile': ['flat', 'eco7', 'smart_tou', 'agile_like']
    },
    notes: 'Determines which electricity rate model is used for heat pump cost projections',
  },

  // ============================================================================
  // CONTROLS PREFERENCE SECTION
  // Required by engine for controls package recommendation (controls enum)
  // ============================================================================
  {
    questionId: 'controlsPackagePreference',
    label: 'Controls package preference',
    section: 'preferences',
    rawPaths: ['controlsPackagePreference'],
    normalizedPaths: ['controlsPackagePreference'],
    engineImpact: ['controls recommendation', 'option scoring'],
    enums: {
      // Matches engine controls enum exactly: none | basic_programmer | smart_zonal | weather_comp | open_therm
      'controlsPackagePreference': ['none', 'basic_programmer', 'smart_zonal', 'weather_comp', 'open_therm']
    },
    notes: 'Derived from temperatureControls field in SystemDetailsData. Maps: basic/programmable-stat → basic_programmer, smart → smart_zonal, load-compensation → weather_comp, smart-load-compensation → open_therm',
  },

  // ============================================================================
  // REQUIRED UPGRADES (engine-only, computed not collected)
  // ============================================================================
  {
    questionId: 'requiredUpgradesForBestCase',
    label: 'Required upgrades for best-case system',
    section: 'emitters',
    rawPaths: ['requiredUpgradesForBestCase'],
    normalizedPaths: ['requiredUpgradesForBestCase'],
    engineImpact: ['upgrade cost estimate', 'best-case scenario scoring'],
    notes: 'Array of upgrade flags: emitters.low_temp | pipework.primary_resize | controls.zoning | mains.pressure_support. Derived from assessment data, not directly asked of user.',
  },
];

/**
 * Get a mapping by question ID
 */
export function getMappingByQuestionId(questionId: string): QuestionMapping | undefined {
  return questionMappings.find(m => m.questionId === questionId);
}

/**
 * Get all mappings for a section
 */
export function getMappingsBySection(section: QuestionSection): QuestionMapping[] {
  return questionMappings.filter(m => m.section === section);
}

/**
 * Get all question IDs
 */
export function getAllQuestionIds(): string[] {
  return questionMappings.map(m => m.questionId);
}

/**
 * Get all raw paths used by any question
 */
export function getAllRawPaths(): string[] {
  const paths = new Set<string>();
  questionMappings.forEach(m => m.rawPaths.forEach(p => paths.add(p)));
  return Array.from(paths);
}

/**
 * Get all normalized paths used by any question
 */
export function getAllNormalizedPaths(): string[] {
  const paths = new Set<string>();
  questionMappings.forEach(m => m.normalizedPaths.forEach(p => paths.add(p)));
  return Array.from(paths);
}

/**
 * Get all required question IDs
 */
export function getRequiredQuestionIds(): string[] {
  return questionMappings.filter(m => m.required).map(m => m.questionId);
}

/**
 * Validate a value against the enum for a question
 */
export function validateEnumValue(questionId: string, rawPath: string, value: unknown): { valid: boolean; message?: string } {
  const mapping = getMappingByQuestionId(questionId);
  if (!mapping) {
    return { valid: false, message: `Question "${questionId}" not found in registry` };
  }

  if (!mapping.enums || !mapping.enums[rawPath]) {
    // No enum defined for this path - any value is valid
    return { valid: true };
  }

  const allowedValues = mapping.enums[rawPath];
  const stringValue = String(value);

  if (!allowedValues.includes(stringValue)) {
    return {
      valid: false,
      message: `Value "${stringValue}" not in allowed values for ${questionId}.${rawPath}: [${allowedValues.join(', ')}]`
    };
  }

  return { valid: true };
}
